import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users, Sparkles, ArrowRight, Play, BarChart3, Brain, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const personas = [
  { name: "Tech-Savvy Millennials", age: "25-34", platform: "Instagram, TikTok", match: "92%" },
  { name: "Business Decision Makers", age: "35-50", platform: "LinkedIn, Twitter", match: "87%" },
  { name: "Gen Z Trendsetters", age: "18-24", platform: "TikTok, YouTube", match: "78%" },
];

const videoFeatures = [
  { icon: Play, title: "Frame-by-frame attention analysis", desc: "Track where viewers look at every second of your video ad" },
  { icon: Brain, title: "Emotional arc mapping", desc: "Measure how emotional engagement rises and falls throughout your video" },
  { icon: BarChart3, title: "Scene-level scoring", desc: "Get individual Neuro Scores for each scene, not just an overall average" },
  { icon: Clock, title: "Drop-off prediction", desc: "Identify the exact moments where viewers are most likely to skip or scroll away" },
];

export default function AudienceSegmentSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { email: email.trim() });
      setStatus({ success: res.data.success, message: res.data.message });
      if (res.data.success) setEmail("");
    } catch {
      setStatus({ success: false, message: "Something went wrong. Please try again." });
    }
    setLoading(false);
  };

  return (
    <section className="py-24 bg-[#F8F9FC]" data-testid="audience-segment-section">
      <div className="max-w-[1200px] mx-auto px-6">

        {/* ═══ AUDIENCE SEGMENTATION (live feature) ═══ */}
        <div className="max-w-4xl mx-auto mb-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>
              AI-Powered <span className="gradient-text">Audience Segmentation</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed max-w-2xl mx-auto" style={{ color: '#475569' }}>
              Our AI analyzes your ad's visual tone, emotional profile, and message complexity to recommend
              the ideal audience segments, platforms, and targeting strategies for each creative.
            </p>
          </motion.div>

          {/* Persona cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            {personas.map((p, i) => (
              <div
                key={p.name}
                data-testid={`persona-card-${i}`}
                className="bg-white rounded-xl border border-slate-200 p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-100 to-fuchsia-100 flex items-center justify-center">
                    <Users size={16} className="text-orange-500" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-700">{p.name}</p>
                    <p className="text-[10px] text-slate-400">Age: {p.age}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-slate-400">{p.platform}</span>
                  <span className="text-xs font-bold text-orange-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {p.match} match
                  </span>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ═══ VIDEO ADS (coming soon) ═══ */}
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-50 border border-fuchsia-100 mb-4">
              <Sparkles size={12} className="text-fuchsia-500" />
              <span className="text-xs font-medium text-fuchsia-600">Coming Soon</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>
              Neuroscience-Powered <span className="gradient-text">Video Analysis</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed max-w-2xl mx-auto" style={{ color: '#475569' }}>
              Upload your video ad and get a second-by-second breakdown of attention, emotion, and engagement.
              Know exactly which frames capture viewers and which ones lose them — before you launch.
            </p>
          </motion.div>

          {/* Video feature cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10"
          >
            {videoFeatures.map((f, i) => (
              <div
                key={f.title}
                className="bg-white rounded-xl border border-slate-200 p-5 opacity-80 hover:opacity-100 transition-opacity duration-300"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-50 to-orange-50 flex items-center justify-center flex-shrink-0">
                    <f.icon size={18} className="text-fuchsia-500" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-700 mb-1">{f.title}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Waitlist form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="text-center text-sm text-slate-500 mb-4">Be the first to know when video analysis launches</p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto" data-testid="waitlist-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="waitlist-email-input"
                className="h-12 rounded-xl border-slate-300 focus:border-orange-400 focus:ring-orange-400"
                required
              />
              <Button
                type="submit"
                data-testid="waitlist-submit"
                disabled={loading}
                className="gradient-btn rounded-xl h-12 px-6 font-semibold whitespace-nowrap"
              >
                <span className="flex items-center gap-2">
                  {loading ? "Joining..." : "Join Waitlist"}
                  <ArrowRight size={16} />
                </span>
              </Button>
            </form>
            {status && (
              <p
                data-testid="waitlist-status"
                className={`text-center text-sm mt-3 ${status.success ? 'text-emerald-600' : 'text-red-500'}`}
              >
                {status.message}
              </p>
            )}
          </motion.div>
        </div>

      </div>
    </section>
  );
}
